/**
 * Auth-aware fetch helper for the feature's TanStack Query hooks.
 *
 * Mirrors the helper in `feature-analytical-variables/src/lib/api/fetchers.ts`
 * — promote to `@verantos-dev/core-utils` once a third feature wants the same
 * shape (per the "compose first, extract on second consumer" rule). Until
 * then, two near-identical copies are cheaper than the cross-package boundary.
 */

import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl, getGatewayApiVersion } from '@verantos-dev/core-constants';

export class AuthFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string
  ) {
    super(`API error: ${status} ${statusText}`);
    this.name = 'AuthFetchError';
  }
}

export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const baseUrl = `${getBaseApiUrl()}/${getGatewayApiVersion()}`;
  const token = await getToken();

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new AuthFetchError(response.status, response.statusText, body);
  }

  // 204 No Content (and the rare 205) carries an empty body — calling
  // ``response.json()`` on it throws SyntaxError, which silently rejects
  // the caller's mutation. Forget/delete-style endpoints rely on this
  // path, so the rejected promise was making cache-invalidation onSuccess
  // hooks never fire and the UI never refresh after a successful delete.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
